'use client'

import React from 'react'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { ScamData } from './entryTypes'

function Field({ label, theme, children }: { label: string; theme: ProcessedTheme; children: React.ReactNode }) {
  const c = watchClasses(theme)
  return (
    <div className="mb-3">
      <div className={`mb-1 text-[12px] font-semibold ${c.label}`}>{label}</div>
      {children}
    </div>
  )
}

function SwitchRow({ label, theme, checked, onCheckedChange }: { label: string; theme: ProcessedTheme; checked: boolean; onCheckedChange: (v: boolean) => void }) {
  const c = watchClasses(theme)
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <span className={`text-[12px] font-semibold ${c.label}`}>{label}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

export function ScamClassificationForm({
  scamData,
  setScamData,
  theme = 'light',
}: {
  scamData: ScamData
  setScamData: React.Dispatch<React.SetStateAction<ScamData>>
  theme?: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const translation = scamData.translation ?? {}

  const setTranslation = (patch: Partial<typeof translation>) =>
    setScamData({ ...scamData, translation: { ...translation, ...patch } })

  return (
    <div className="p-1">
      <div className={`mb-2 text-[11px] font-mono uppercase tracking-widest ${c.faint}`}>Scam</div>

      <SwitchRow label="É burla?" theme={theme} checked={scamData.isScam ?? false} onCheckedChange={(v) => setScamData({ ...scamData, isScam: v })} />
      <SwitchRow
        label="Esconder detalhes do pipeline (passos de análise)"
        theme={theme}
        checked={scamData.hideClassification ?? false}
        onCheckedChange={(v) => setScamData({ ...scamData, hideClassification: v })}
      />
      <SwitchRow
        label="Mostrar aviso de informação insuficiente"
        theme={theme}
        checked={scamData.hidden ?? false}
        onCheckedChange={(v) => setScamData({ ...scamData, hidden: v })}
      />

      <Field label="Scam Value" theme={theme}>
        <Input
          className={c.input}
          type="number"
          value={scamData.scamValue ?? ''}
          onChange={(e) => setScamData({ ...scamData, scamValue: e.target.value ? Number(e.target.value) : undefined })}
          placeholder="Enter value"
        />
      </Field>

      <Field label="Scam Claims (separado por vírgulas)" theme={theme}>
        <Textarea
          className={c.input}
          value={scamData.scamClaims?.join(', ') ?? ''}
          onChange={(e) => setScamData({ ...scamData, scamClaims: e.target.value.split(',').map((s) => s.trim()) })}
          placeholder="e.g., fake, fraud"
        />
      </Field>

      <div className={`mb-2 mt-4 text-[11px] font-mono uppercase tracking-widest ${c.faint}`}>Translation</div>

      <Field label="Verdict Label" theme={theme}>
        <Input
          className={c.input}
          value={translation.verdict_label ?? ''}
          onChange={(e) => setTranslation({ verdict_label: e.target.value })}
          placeholder="e.g., Provável fraude"
        />
      </Field>

      <Field label="Summary" theme={theme}>
        <Input
          className={c.input}
          value={translation.summary ?? ''}
          onChange={(e) => setTranslation({ summary: e.target.value })}
          placeholder="One-line summary"
        />
      </Field>

      <Field label="Explanation" theme={theme}>
        <Textarea
          className={c.input}
          value={translation.explanation ?? ''}
          onChange={(e) => setTranslation({ explanation: e.target.value })}
          placeholder="Full explanation"
        />
      </Field>

      <Field label="Recommendations (uma por linha)" theme={theme}>
        <Textarea
          className={c.input}
          value={translation.recommendations?.join('\n') ?? ''}
          onChange={(e) => setTranslation({ recommendations: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
          placeholder="Each recommendation on a new line"
        />
      </Field>
    </div>
  )
}
